// @ts-nocheck
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { 
  Upload, FileText, Users, Image, Loader2, 
  Download, ArrowLeft, X, Check, User
} from 'lucide-react';
import Papa from 'papaparse';
import jsPDF from 'jspdf';

interface RosterPlayer {
  name: string;
  number: string;
  position: string;
  height: string;
  weight: string;
  gradYear: string;
  gpa: string;
  email: string;
  phone: string;
}

interface CoachInfo {
  name: string;
  title: string;
  email: string;
  phone: string;
  college: string;
  bio: string;
}

const DemoPacketGenerator: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // State
  const [roster, setRoster] = useState<RosterPlayer[]>([]);
  const [coachInfo, setCoachInfo] = useState<CoachInfo>({
    name: '',
    title: '',
    email: '',
    phone: '',
    college: '',
    bio: ''
  });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [generating, setGenerating] = useState(false);

  // CSV Upload Handler
  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const players: RosterPlayer[] = results.data.map((row: any) => ({
          name: row.name || row.Name || row.player_name || '',
          number: row.number || row.Number || row.jersey_number || '',
          position: row.position || row.Position || '',
          height: row.height || row.Height || '',
          weight: row.weight || row.Weight || '',
          gradYear: row.grad_year || row.gradYear || row.GradYear || row.graduation_year || '',
          gpa: row.gpa || row.GPA || '',
          email: row.email || row.Email || '',
          phone: row.phone || row.Phone || row.cell || ''
        }));
        setRoster(players);
        toast.success(`Loaded ${players.length} players from CSV`);
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        toast.error('Failed to parse CSV file');
      }
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Logo Upload Handler
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingLogo(true);
    try {
      const fileName = `demo-logos/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('event-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('event-images')
        .getPublicUrl(fileName);

      setLogoUrl(publicUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  // Remove Logo
  const handleRemoveLogo = () => {
    setLogoUrl(null);
  };

  // Add sample roster data
  const addSampleRoster = () => {
    const samplePlayers: RosterPlayer[] = [
      { name: 'John Smith', number: '10', position: 'Point Guard', height: "6'1\"", weight: '175', gradYear: '2025', gpa: '3.8', email: 'john@example.com', phone: '555-0101' },
      { name: 'Mike Johnson', number: '23', position: 'Shooting Guard', height: "6'4\"", weight: '190', gradYear: '2025', gpa: '3.5', email: 'mike@example.com', phone: '555-0102' },
      { name: 'David Williams', number: '34', position: 'Small Forward', height: "6'6\"", weight: '210', gradYear: '2026', gpa: '3.9', email: 'david@example.com', phone: '555-0103' },
      { name: 'Chris Brown', number: '42', position: 'Power Forward', height: "6'8\"", weight: '225', gradYear: '2025', gpa: '3.2', email: 'chris@example.com', phone: '555-0104' },
      { name: 'James Davis', number: '55', position: 'Center', height: "6'10\"", weight: '245', gradYear: '2026', gpa: '3.6', email: 'james@example.com', phone: '555-0105' },
    ];
    setRoster(samplePlayers);
    toast.success('Sample roster loaded');
  };

  // Generate Packet PDF
  const handleGeneratePacket = async () => {
    if (roster.length === 0) {
      toast.error('Please upload a roster first');
      return;
    }

    setGenerating(true);
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      // Add logo if exists
      if (logoUrl) {
        try {
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject();
            img.src = logoUrl;
          });
          // Add logo centered at top
          const logoWidth = 40;
          const logoHeight = (img.height / img.width) * logoWidth;
          pdf.addImage(img, 'PNG', (pageWidth - logoWidth) / 2, yPosition, logoWidth, logoHeight);
          yPosition += logoHeight + 10;
        } catch {
          console.log('Could not load logo for PDF');
        }
      }

      // Title
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Team Roster Packet', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Coach Info Section
      if (coachInfo.name) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Coach Information', margin, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Name: ${coachInfo.name}`, margin, yPosition);
        yPosition += 6;
        if (coachInfo.title) {
          pdf.text(`Title: ${coachInfo.title}`, margin, yPosition);
          yPosition += 6;
        }
        if (coachInfo.college) {
          pdf.text(`College: ${coachInfo.college}`, margin, yPosition);
          yPosition += 6;
        }
        if (coachInfo.email) {
          pdf.text(`Email: ${coachInfo.email}`, margin, yPosition);
          yPosition += 6;
        }
        if (coachInfo.phone) {
          pdf.text(`Phone: ${coachInfo.phone}`, margin, yPosition);
          yPosition += 6;
        }
        if (coachInfo.bio) {
          yPosition += 4;
          const bioLines = pdf.splitTextToSize(coachInfo.bio, pageWidth - margin * 2);
          pdf.text(bioLines, margin, yPosition);
          yPosition += bioLines.length * 5;
        }
        yPosition += 10;
      }

      // Roster Table
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Player Roster', margin, yPosition);
      yPosition += 10;

      // Table headers
      const colWidths = [15, 50, 35, 25, 25, 25];
      const headers = ['#', 'Name', 'Position', 'Height', 'Grad', 'GPA'];
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      let xPos = margin;
      headers.forEach((header, idx) => {
        pdf.text(header, xPos, yPosition);
        xPos += colWidths[idx];
      });
      yPosition += 6;
      
      // Draw header line
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPosition - 3, pageWidth - margin, yPosition - 3);

      // Table data
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      
      roster.forEach((player, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - margin - 20) {
          pdf.addPage();
          yPosition = margin;
        }
        
        xPos = margin;
        const rowData = [
          player.number || '-',
          player.name || '-',
          player.position || '-',
          player.height || '-',
          player.gradYear || '-',
          player.gpa || '-'
        ];
        
        rowData.forEach((data, idx) => {
          const text = data.length > 15 ? data.substring(0, 14) + '...' : data;
          pdf.text(text, xPos, yPosition);
          xPos += colWidths[idx];
        });
        yPosition += 6;
      });

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(128);
      pdf.text(`Generated by RecruitLook - ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

      // Download the PDF
      pdf.save('team-roster-packet.pdf');
      toast.success('Packet downloaded successfully!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate packet');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/demo/hub')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Demo Hub
        </Button>
      </div>

      <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-8">
        <h1 className="text-2xl font-bold text-yellow-800">📋 Coach Packet Generator</h1>
        <p className="text-yellow-700">
          Simulate creating a roster packet with CSV upload, coach info, and branding.
        </p>
      </div>

      <Tabs defaultValue="roster" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="roster" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Roster Upload
          </TabsTrigger>
          <TabsTrigger value="coach" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Coach Info
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            Logo & Branding
          </TabsTrigger>
        </TabsList>

        {/* Roster Upload Tab */}
        <TabsContent value="roster" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upload Roster CSV</CardTitle>
              <CardDescription>
                Upload a CSV file with player data. Expected columns: name, number, position, height, weight, grad_year, gpa, email, phone
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCSVUpload}
                  className="hidden"
                />
                <Button onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload CSV
                </Button>
                <Button variant="outline" onClick={addSampleRoster}>
                  <FileText className="w-4 h-4 mr-2" />
                  Load Sample Data
                </Button>
                {roster.length > 0 && (
                  <Button variant="destructive" onClick={() => setRoster([])}>
                    <X className="w-4 h-4 mr-2" />
                    Clear Roster
                  </Button>
                )}
              </div>

              {roster.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="font-medium">{roster.length} players loaded</span>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2">#</th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Position</th>
                          <th className="text-left p-2">Height</th>
                          <th className="text-left p-2">Grad Year</th>
                          <th className="text-left p-2">GPA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roster.map((player, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{player.number}</td>
                            <td className="p-2">{player.name}</td>
                            <td className="p-2">{player.position}</td>
                            <td className="p-2">{player.height}</td>
                            <td className="p-2">{player.gradYear}</td>
                            <td className="p-2">{player.gpa}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coach Info Tab */}
        <TabsContent value="coach" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Coach Information Center</CardTitle>
              <CardDescription>
                Enter coach details to be displayed on the packet
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="coachName">Coach Name</Label>
                  <Input
                    id="coachName"
                    value={coachInfo.name}
                    onChange={(e) => setCoachInfo({ ...coachInfo, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coachTitle">Title/Position</Label>
                  <Input
                    id="coachTitle"
                    value={coachInfo.title}
                    onChange={(e) => setCoachInfo({ ...coachInfo, title: e.target.value })}
                    placeholder="Head Coach"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coachEmail">Email</Label>
                  <Input
                    id="coachEmail"
                    type="email"
                    value={coachInfo.email}
                    onChange={(e) => setCoachInfo({ ...coachInfo, email: e.target.value })}
                    placeholder="coach@university.edu"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coachPhone">Phone</Label>
                  <Input
                    id="coachPhone"
                    value={coachInfo.phone}
                    onChange={(e) => setCoachInfo({ ...coachInfo, phone: e.target.value })}
                    placeholder="555-123-4567"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="coachCollege">College/University</Label>
                  <Input
                    id="coachCollege"
                    value={coachInfo.college}
                    onChange={(e) => setCoachInfo({ ...coachInfo, college: e.target.value })}
                    placeholder="State University"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="coachBio">Bio/Notes</Label>
                  <Textarea
                    id="coachBio"
                    value={coachInfo.bio}
                    onChange={(e) => setCoachInfo({ ...coachInfo, bio: e.target.value })}
                    placeholder="Brief bio or notes about the coach..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Preview */}
              {coachInfo.name && (
                <div className="mt-6 p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Preview</h4>
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold">{coachInfo.name}</p>
                      <p className="text-muted-foreground">{coachInfo.title}</p>
                      <p className="text-sm">{coachInfo.college}</p>
                      <p className="text-sm text-muted-foreground">{coachInfo.email} | {coachInfo.phone}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logo & Branding Tab */}
        <TabsContent value="branding" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Logo Upload</CardTitle>
              <CardDescription>
                Upload your team or organization logo to appear on the packet
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button 
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </>
                  )}
                </Button>
              </div>

              {logoUrl && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="font-medium">Logo uploaded</span>
                  </div>
                  <div className="relative inline-block">
                    <img 
                      src={logoUrl} 
                      alt="Uploaded logo" 
                      className="max-w-xs max-h-48 object-contain border rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={handleRemoveLogo}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {!logoUrl && (
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                  <Image className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">No logo uploaded yet</p>
                  <p className="text-sm text-muted-foreground">Click "Upload Logo" to add your branding</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate Button */}
      <Card className="mt-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Ready to Generate?</h3>
              <div className="flex gap-2 mt-2">
                <Badge variant={roster.length > 0 ? "default" : "secondary"}>
                  {roster.length > 0 ? `✓ ${roster.length} Players` : "No Roster"}
                </Badge>
                <Badge variant={coachInfo.name ? "default" : "secondary"}>
                  {coachInfo.name ? "✓ Coach Info" : "No Coach Info"}
                </Badge>
                <Badge variant={logoUrl ? "default" : "secondary"}>
                  {logoUrl ? "✓ Logo" : "No Logo"}
                </Badge>
              </div>
            </div>
            <Button 
              size="lg" 
              onClick={handleGeneratePacket}
              disabled={generating || roster.length === 0}
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Generate Packet
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoPacketGenerator;